<template>
  <div class="conv-thread" :class="{ 'conv-thread--compact': compact }">
    <div v-if="!conversation.messages.length" class="conv-thread__empty">Aucun motif renseigné</div>

    <div v-for="msg in conversation.messages" :key="msg.id" class="conv-msg"
      :class="[`conv-msg--${msg.role}`, !compact && isMine(msg) ? 'conv-msg--mine' : 'conv-msg--theirs']">
      <div v-if="!compact" class="conv-msg__avatar" :class="`conv-msg__avatar--${msg.role}`" aria-hidden="true">
        <v-icon v-if="msg.role === 'admin'" size="16" color="white" icon="mdi-shield-account" />
        <span v-else>{{ initials }}</span>
      </div>

      <div class="conv-msg__body">
        <div class="conv-msg__meta">
          <span class="conv-msg__author">{{ authorLabel(msg) }}</span>
          <span v-if="!compact && msg === conversation.motifMessage" class="conv-msg__tag">Motif</span>
          <span v-if="dateLabel(msg)" class="conv-msg__date">{{ dateLabel(msg) }}</span>
        </div>
        <div class="conv-msg__bubble">{{ msg.text }}</div>
      </div>
    </div>

    <div v-if="showNoReply && conversation.messages.length && !conversation.replyCount" class="conv-thread__no-reply">
      <v-icon size="14" icon="mdi-clock-outline" />
      Aucune réponse pour le moment
    </div>
  </div>
</template>

<script>
import { getConversation, formatMessageDate } from "@/core/commentaire";

/**
 * Affiche le commentaire d'une demande sous forme de conversation (bulles).
 * - mode normal : disposition messagerie, mes messages à droite ;
 * - mode « compact » : résumé aligné à gauche, pour les fenêtres de récapitulatif.
 */
export default {
  name: "ConversationThread",
  props: {
    commentaire: { type: [String, Array, Object, Number], default: "" },
    // Qui consulte : « user » (Mon espace) ou « admin » (zone admin)
    viewer: { type: String, default: "user" },
    // Nom de l'employé à l'origine de la demande
    employeeName: { type: String, default: "" },
    compact: { type: Boolean, default: false },
    // Affiche « Aucune réponse pour le moment » quand il n'y a que le motif
    showNoReply: { type: Boolean, default: false },
  },
  computed: {
    conversation() {
      return getConversation(this.commentaire);
    },
    initials() {
      return (this.employeeName || "")
        .split(" ")
        .filter(Boolean)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "?";
    },
  },
  methods: {
    isMine(msg) {
      return msg.role === this.viewer;
    },
    authorLabel(msg) {
      if (msg.role === "user") {
        if (this.viewer === "user") return "Vous";
        return msg.author || this.employeeName || "Employé";
      }
      return msg.author || msg.label;
    },
    dateLabel(msg) {
      return formatMessageDate(msg);
    },
  },
};
</script>

<style scoped>
.conv-thread {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.conv-thread--compact {
  gap: 8px;
}

.conv-thread__empty {
  font-size: 13px;
  color: #9e9e9e;
  font-style: italic;
}

/* ── Message ── */
.conv-msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 100%;
}

.conv-msg--mine {
  flex-direction: row-reverse;
}

.conv-msg__avatar {
  width: 30px;
  height: 30px;
  margin-bottom: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
}

.conv-msg__avatar--user {
  background: linear-gradient(135deg, #3f51b5, #283593);
}

.conv-msg__avatar--admin {
  background: linear-gradient(135deg, #455a64, #1a1a2e);
}

.conv-msg__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  max-width: min(85%, 400px);
}

.conv-msg--mine .conv-msg__body {
  align-items: flex-end;
}

.conv-msg__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 6px;
  padding: 0 4px;
  font-size: 11.5px;
  color: #9e9e9e;
}

.conv-msg--mine .conv-msg__meta {
  justify-content: flex-end;
}

.conv-msg__author {
  font-weight: 700;
  color: #424242;
}

.conv-msg__tag {
  padding: 0 7px;
  border-radius: 999px;
  background: #e8eaf6;
  color: #3f51b5;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.conv-msg__bubble {
  padding: 9px 13px;
  border-radius: 16px;
  font-size: 13.5px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.conv-msg--theirs .conv-msg__bubble {
  background: #f1f3f9;
  color: #1a1a2e;
  border-bottom-left-radius: 4px;
}

.conv-msg--mine .conv-msg__bubble {
  background: #3f51b5;
  color: #fff;
  border-bottom-right-radius: 4px;
}

/* ── Mode compact (récapitulatifs) ── */
.conv-thread--compact .conv-msg__body {
  max-width: 100%;
}

.conv-thread--compact .conv-msg__meta {
  padding: 0 2px;
  font-size: 11px;
}

.conv-thread--compact .conv-msg__bubble {
  padding: 7px 11px;
  font-size: 13px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
}

.conv-thread--compact .conv-msg--user .conv-msg__bubble {
  background: #eef0fb;
  color: #1a1a2e;
}

.conv-thread--compact .conv-msg--admin .conv-msg__bubble {
  background: #fff;
  color: #1a1a2e;
  border: 1px solid #e6e8f0;
}

/* ── Aucune réponse ── */
.conv-thread__no-reply {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  padding: 4px 12px;
  border-radius: 999px;
  background: #f5f5f7;
  color: #9e9e9e;
  font-size: 12px;
}
</style>
